// src/utils/emailService.js
import { db } from '../config/firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';

const EMAILJS_SERVICE_ID = 'service_brixw3e';
const EMAILJS_TEMPLATE_ID = 'template_g27bkf6';
const EMAILJS_NOREPLY_TEMPLATE_ID = 'template_xd3rylz';
const EMAILJS_PUBLIC_KEY = 'AjMRD20n1kik5NtJD';

// Existing admin notification function
export const sendEmailNotification = async (subject, htmlContent) => {
  try {
    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          to_email: 'team@planaidesign.com',
          subject: subject,
          html_content: htmlContent,
          reply_to: 'noreply@planaidesign.com'
        }
      })
    });

    if (response.ok) {
      console.log('Email notification sent successfully via EmailJS');
      return { success: true };
    } else {
      const errorText = await response.text();
      console.error('EmailJS send failed:', response.status, errorText);
      return { success: false, error: errorText };
    }
  } catch (error) {
    console.error('EmailJS error (non-critical):', error);
    return { success: false, error };
  }
};

// Send welcome email to new users
export const sendWelcomeEmail = async (userData) => {
  console.log('=== WELCOME EMAIL START ===');
  console.log('User data received:', userData);
  try {
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1e40af;">Welcome to planAIdesign, ${userData.name}!</h2>
        
        <p>Thank you for joining planAIdesign. We're excited to help you bring your development project to life.</p>
        
        <h3 style="color: #1e40af;">Getting Started:</h3>
        <ul>
          <li><strong>Stage 1 (Site Investigation & Feasibility)</strong> - $149-$249<br>
          Check if your project is viable before investing in design work</li>
          <li><strong>Stage 2 (Concept Design)</strong> - $299-$1,099<br>
          Get professional 3D renders and detailed floor plans</li>
        </ul>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="https://planaidesign.com.au" style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block;">
            Create Your First Project
          </a>
        </div>
        
        <hr style="border: 0; border-top: 1px solid #e5e5e5; margin: 40px 0;">
        
        <p style="color: #999; font-size: 12px; text-align: center;">
          This is an automated message from planAIdesign. Please do not reply to this email.
        </p>
      </div>
    `;
    console.log('Preparing to send email to:', userData.email);

    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_NOREPLY_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          to_email: userData.email,
          subject: 'Welcome to planAIdesign - Let\'s Get Started!',
          html_content: htmlContent,
          from_name: 'planAIdesign',
          from_email: 'noreply@planaidesign.com',
          reply_to: 'noreply@planaidesign.com'
        }
      })
    });

    if (response.ok) {
      console.log('✓ Welcome email sent successfully to:', userData.email);
      console.log('Welcome email sent to:', userData.email);
      const emailRecord = {
        email: userData.email,
        type: 'welcome',
        sentAt: new Date().toISOString()
      };
      const existingRecords = JSON.parse(localStorage.getItem('email_records') || '[]');
      existingRecords.push(emailRecord);
      localStorage.setItem('email_records', JSON.stringify(existingRecords));
      return { success: true };
    } else {
      const errorText = await response.text();  
      console.error('✗ EmailJS error response:', errorText);
      console.error('Failed to send welcome email');
      return { success: false };
    }
  } catch (error) {
    console.error('Welcome email error:', error);
    return { success: false, error };
  }
};

// Send follow-up emails based on user activity
export const sendFollowUpEmail = async (userData, emailType) => {
  const emailTemplates = {
    day3: {
      subject: 'Understanding Your Development Potential',
      content: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #1e40af;">Hi ${userData.name}, Still Planning Your Project?</h2>
          
          <p>It's been a few days since you joined planAIdesign. Here's what Stage 1 can tell you about your site:</p>
          
          <ul style="line-height: 1.8;">
            <li>✓ Maximum building envelope for your site</li>
            <li>✓ Planning constraints that could affect your project</li>
            <li>✓ Development potential analysis</li>
            <li>✓ Clear recommendations for next steps</li>
          </ul>
          
          <p style="background-color: #dbeafe; padding: 15px; border-radius: 5px;">
            <strong>Quick turnaround:</strong> Most feasibility reports are delivered within 5-7 business days.
          </p>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="https://planaidesign.com.au" style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block;">
              Start Your Feasibility Study
            </a>
          </div>
          
          <hr style="border: 0; border-top: 1px solid #e5e5e5; margin: 40px 0;">
          
          <p style="color: #999; font-size: 12px; text-align: center;">
            This is an automated message. Please do not reply to this email.
          </p>
        </div>
      `
    },
    day7: {
      subject: 'Quick Question About Your Project',
      content: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #1e40af;">Hi ${userData.name},</h2>
          
          <p>I wanted to check in about your development project.</p>
          
          <p>Many clients start with Stage 1 because it helps them:</p>
          <ul>
            <li>Avoid costly design work on unbuildable projects</li>
            <li>Understand exactly what they can build</li>
            <li>Make informed decisions about their investment</li>
          </ul>
          
          <p><strong>The process is straightforward:</strong></p>
          <ol>
            <li>Submit your project details (5 minutes)</li>
            <li>Provide your card details to secure your project</li>
            <li>Receive your feasibility report (5-7 business days)</li>
          </ol>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="https://planaidesign.com.au" style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block;">
              Get Started Today
            </a>
          </div>
          
          <hr style="border: 0; border-top: 1px solid #e5e5e5; margin: 40px 0;">
          
          <p style="color: #999; font-size: 12px; text-align: center;">
            This is an automated message. Please do not reply to this email.
          </p>
        </div>
      `
    },
    day14: {
      subject: 'Your Development Project - Final Check-in',
      content: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #1e40af;">Hi ${userData.name},</h2>
          
          <p>This is my final automated email about your development project.</p>
          
          <p>If you're still planning, remember that Stage 1 (Site Investigation & Feasibility) is the foundation of any successful development:</p>
          
          <ul>
            <li>Professional feasibility assessment ($149-$249)</li>
            <li>3-5 business day turnaround</li>
            <li>Clear, actionable recommendations</li>
          </ul>
          
          <p>If planAIdesign isn't the right fit for your project, that's completely fine. We wish you the best with your development.</p>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="https://planaidesign.com.au" style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block;">
              Create Your Project
            </a>
          </div>
          
          <hr style="border: 0; border-top: 1px solid #e5e5e5; margin: 40px 0;">
          
          <p style="color: #999; font-size: 12px; text-align: center;">
            This is the last automated email you'll receive from us.<br>
            Please do not reply to this email.
          </p>
        </div>
      `
    }
  };

  const template = emailTemplates[emailType];
  if (!template) return { success: false, error: 'Invalid email type' };

  try {
    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_NOREPLY_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          to_email: userData.email,
          subject: template.subject,
          html_content: template.content,
          from_name: 'planAIdesign',
          from_email: 'noreply@planaidesign.com',
          reply_to: 'noreply@planaidesign.com'
        }
      })
    });

    if (response.ok) {
      console.log(`${emailType} email sent to:`, userData.email);
      const emailRecord = {
        email: userData.email,
        type: emailType,
        sentAt: new Date().toISOString()
      };
      const existingRecords = JSON.parse(localStorage.getItem('email_records') || '[]');
      existingRecords.push(emailRecord);
      localStorage.setItem('email_records', JSON.stringify(existingRecords));
      return { success: true };
    } else {
      console.error(`Failed to send ${emailType} email`);
      return { success: false };
    }
  } catch (error) {
    console.error(`${emailType} email error:`, error);
    return { success: false, error };
  }
};

// ============================================
// Helper: Get payment amount by project type
// ============================================
const getPaymentAmountByType = (projectType, townhouseUnits) => {
  // Stage 1 Pricing (from PricingTableModal.js)
  // Single Dwelling: $199
  // Duplex: $249
  // Townhouse (3-5 Units): $499
  // Townhouse (6-9 Units): $799
  // Secondary Dwelling: $149
  // Minor Renovation: $149
  // Major Renovation: $199
  
  const typeMap = {
    // Single Dwelling: $199
    'single': 199,
    'single-dwelling': 199,
    'single_dwelling': 199,
    'singledwelling': 199,
    
    // Duplex: $249
    'duplex': 249,
    'dual-occupancy': 249,
    'dual_occupancy': 249,
    'dualoccupancy': 249,
    
    // Secondary Dwelling (Granny Flat): $149
    'secondary-dwelling': 149,
    'secondary_dwelling': 149,
    'secondarydwelling': 149,
    'granny-flat': 149,
    'granny_flat': 149,
    'grannyflat': 149,
    
    // Minor Renovation: $149
    'minor-renovation': 149,
    'minor_renovation': 149,
    'minorrenovation': 149,
    
    // Major Renovation: $199
    'major-renovation': 199,
    'major_renovation': 199,
    'majorrenovation': 199,
    
    // Townhouse handled separately based on units
    'townhouse': 499  // Default to 3-5 units price
  };
  
  // Normalize the project type for matching
  const normalizedType = (projectType || '').toLowerCase().replace(/\s+/g, '-');
  
  // Special handling for townhouse based on unit count
  if (normalizedType === 'townhouse' || normalizedType.includes('townhouse')) {
    const units = townhouseUnits || '3-5';
    // Check if 6-9 units
    if (units === '6-9' || units.includes('6') || parseInt(units) >= 6) {
      return 799;  // Townhouse (6-9 Units)
    }
    return 499;  // Townhouse (3-5 Units)
  }
  
  return typeMap[normalizedType] || 199; // Default to $199 (Single Dwelling price)
};

// ============================================
// NEW: Stage 1 Payment Reminder Email (Day 3)
// ============================================
export const sendPaymentReminderEmail = async (project) => {
  try {
    // Get correct payment amount based on project type (and townhouse units if applicable)
    const paymentAmount = project.paymentAmount || getPaymentAmountByType(project.type, project.townhouseUnits);
    
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1e40af;">Payment Reminder</h2>
        
        <p>Hi ${project.client},</p>
        
        <p>Your Final Site Investigation & Feasibility Report for <strong>${project.name}</strong> is ready for release.</p>
        
        <div style="background-color: #fef3c7; padding: 15px; border-radius: 5px; border-left: 4px solid #f59e0b; margin: 20px 0;">
          <p style="margin: 0;"><strong>Amount due:</strong> $${paymentAmount}</p>
          <p style="margin: 5px 0 0 0;"><strong>Due:</strong> Within 2 business days</p>
        </div>
        
        <p>If payment is not received within 2 business days, your saved card will be automatically charged and the Final Report will be released.</p>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${project.paymentLink || 'https://www.planaidesign.com.au'}" style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block;">
            Pay Now
          </a>
        </div>
        
        <p style="color: #666;">If you have already made payment, please disregard this email.</p>
        
        <hr style="border: 0; border-top: 1px solid #e5e5e5; margin: 40px 0;">
        
        <p style="color: #999; font-size: 12px; text-align: center;">
          This is an automated message from planAIdesign.<br>
          Please do not reply to this email.
        </p>
      </div>
    `;

    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_NOREPLY_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          to_email: project.email,
          subject: `Payment Reminder - ${project.name}`,
          html_content: htmlContent,
          from_name: 'planAIdesign',
          from_email: 'noreply@planaidesign.com',
          reply_to: 'noreply@planaidesign.com'
        }
      })
    });

    if (response.ok) {
      console.log('✓ Payment reminder email sent to:', project.email);
      return { success: true };
    } else {
      const errorText = await response.text();
      console.error('✗ Payment reminder email failed:', errorText);
      return { success: false, error: errorText };
    }
  } catch (error) {
    console.error('Payment reminder email error:', error);
    return { success: false, error };
  }
};

// ============================================
// UPDATED: Check and send scheduled emails
// Now includes Stage 1 payment reminders
// ============================================
export const checkAndSendScheduledEmails = async () => {
  console.log('=== CHECKING SCHEDULED EMAILS ===');
  
  // ============================================
  // EXISTING: New user follow-up emails
  // ============================================
  const users = JSON.parse(localStorage.getItem('registered_users') || '[]');
  const emailRecords = JSON.parse(localStorage.getItem('email_records') || '[]');
  
  for (const user of users) {
    if (!user.email || !user.registrationDate) continue;
    
    const daysSinceRegistration = Math.floor(
      (Date.now() - new Date(user.registrationDate)) / (1000 * 60 * 60 * 24)
    );
    
    const sentEmails = emailRecords
      .filter(record => record.email === user.email)
      .map(record => record.type);
    
    const projects = JSON.parse(localStorage.getItem('projects') || '[]');
    const userHasProjects = projects.some(p => p.email === user.email);
    
    if (userHasProjects) continue;
    
    if (daysSinceRegistration >= 3 && !sentEmails.includes('day3')) {
      await sendFollowUpEmail(user, 'day3');
    }
    if (daysSinceRegistration >= 7 && !sentEmails.includes('day7')) {
      await sendFollowUpEmail(user, 'day7');
    }
    if (daysSinceRegistration >= 14 && !sentEmails.includes('day14')) {
      await sendFollowUpEmail(user, 'day14');
    }
  }

  // ============================================
  // NEW: Stage 1 Payment Reminder (Day 3 after invoice)
  // ============================================
  try {
    console.log('Checking for Stage 1 payment reminders...');
    
    // Query Stage 1 projects with payment-due status
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('stage', '==', 1),
      where('paymentStatus', 'in', ['payment-due', 'invoice-sent'])
    );
    
    const snapshot = await getDocs(q);
    console.log(`Found ${snapshot.size} Stage 1 projects awaiting payment`);
    
    for (const docSnapshot of snapshot.docs) {
      const project = { id: docSnapshot.id, ...docSnapshot.data() };
      
      // Skip if no invoice date or email
      if (!project.invoiceSentAt || !project.email) {
        console.log(`Skipping project ${project.id}: missing invoiceSentAt or email`);
        continue;
      }
      
      // Calculate days since invoice
      const invoiceDate = new Date(project.invoiceSentAt);
      const daysSinceInvoice = Math.floor(
        (Date.now() - invoiceDate) / (1000 * 60 * 60 * 24)
      );
      
      console.log(`Project ${project.name}: ${daysSinceInvoice} days since invoice`);
      
      // Check if reminder already sent (stored in Firebase)
      if (project.paymentReminderSentAt) {
        console.log(`Reminder already sent for project ${project.id}`);
        continue;
      }
      
      // Send reminder on Day 3 (but not after Day 5 - auto-charge handles that)
      if (daysSinceInvoice >= 3 && daysSinceInvoice < 5) {
        console.log(`Sending Day 3 payment reminder for: ${project.name}`);
        const result = await sendPaymentReminderEmail(project);
        
        if (result.success) {
          // Mark reminder as sent in Firebase
          const projectRef = doc(db, 'projects', project.id);
          await updateDoc(projectRef, {
            paymentReminderSentAt: new Date().toISOString()
          });
          console.log(`✓ Reminder sent and recorded for project ${project.id}`);
        }
      }
    }
  } catch (error) {
    console.error('Error checking payment reminders:', error);
  }

  // ============================================
  // NEW: Auto-charge confirmation emails
  // (sent from frontend after server auto-charges)
  // ============================================
  try {
    console.log('Checking for auto-charge confirmation emails...');
    
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('autoChargeEmailPending', '==', true)
    );
    
    const snapshot = await getDocs(q);
    console.log(`Found ${snapshot.size} projects needing auto-charge confirmation email`);
    
    for (const docSnapshot of snapshot.docs) {
      const project = { id: docSnapshot.id, ...docSnapshot.data() };
      
      if (!project.email) {
        console.log(`Skipping project ${project.id}: missing email`);
        continue;
      }
      
      // Clear flag FIRST to prevent duplicate sends on re-render/reload
      const projectRef = doc(db, 'projects', project.id);
      await updateDoc(projectRef, {
        autoChargeEmailPending: false,
        autoChargeEmailSentAt: new Date().toISOString()
      });
      
      console.log(`Sending auto-charge confirmation for: ${project.name}`);
      const result = await sendAutoChargeConfirmationEmail(project);
      
      if (result.success) {
        console.log(`✓ Auto-charge confirmation sent for project ${project.id}`);
      } else {
        // Email failed — set flag back so it retries next time
        console.error(`✗ Email failed for project ${project.id}, resetting flag for retry`);
        await updateDoc(projectRef, {
          autoChargeEmailPending: true,
          autoChargeEmailSentAt: null
        });
      }
    }
  } catch (error) {
    console.error('Error checking auto-charge emails:', error);
  }
  
  console.log('=== SCHEDULED EMAIL CHECK COMPLETE ===');
};

// ============================================
// NEW: Auto-charge Confirmation Email
// ============================================
export const sendAutoChargeConfirmationEmail = async (project) => {
  try {
    const amountCharged = project.autoChargedAmount || getPaymentAmountByType(project.type, project.townhouseUnits);
    
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1e40af;">Payment Confirmed</h2>
        
        <p>Hi ${project.client || 'there'},</p>
        
        <p>Your payment for <strong>${project.name}</strong> has been successfully processed.</p>
        
        <div style="background-color: #d1fae5; padding: 15px; border-radius: 5px; border-left: 4px solid #10b981; margin: 20px 0;">
          <p style="margin: 0;"><strong>Amount charged:</strong> $${amountCharged}</p>
          <p style="margin: 5px 0 0 0;"><strong>Payment method:</strong> Card on file</p>
        </div>
        
        <p>Your <strong>Final Site Investigation & Feasibility Report</strong> is now available for download in your dashboard.</p>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="https://www.planaidesign.com.au" style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block;">
            View Your Report
          </a>
        </div>
        
        <p style="color: #666;">Thank you for choosing planAIdesign.</p>
        
        <hr style="border: 0; border-top: 1px solid #e5e5e5; margin: 40px 0;">
        
        <p style="color: #999; font-size: 12px; text-align: center;">
          This is an automated message from planAIdesign.<br>
          Please do not reply to this email.
        </p>
      </div>
    `;

    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_NOREPLY_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          to_email: project.email,
          subject: `Payment Confirmed - ${project.name}`,
          html_content: htmlContent,
          from_name: 'planAIdesign',
          from_email: 'noreply@planaidesign.com',
          reply_to: 'noreply@planaidesign.com'
        }
      })
    });

    if (response.ok) {
      console.log('✓ Auto-charge confirmation email sent to:', project.email);
      return { success: true };
    } else {
      const errorText = await response.text();
      console.error('✗ Auto-charge confirmation email failed:', errorText);
      return { success: false, error: errorText };
    }
  } catch (error) {
    console.error('Auto-charge confirmation email error:', error);
    return { success: false, error };
  }
};